import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { useCopyToClipboard } from '../lib/hooks'
import { Button } from './Button'
import { IconCheck, IconCopy } from './icons'

export interface CodeBlockProps {
  code: string
  /** Shown in the header strip, such as "Workflow definition". */
  title?: ReactNode
  /** Wrap long lines instead of scrolling sideways. */
  wrap?: boolean
  copyable?: boolean
  /** Highlights these one-based lines, used by the knowledge editor for errors. */
  highlightLines?: number[]
  showLineNumbers?: boolean
  maxHeight?: string
  className?: string
}

/** Monospace block for a workflow definition, a payload or a copyable secret. */
export function CodeBlock({
  code,
  title,
  wrap = false,
  copyable = true,
  highlightLines,
  showLineNumbers = false,
  maxHeight = '28rem',
  className,
}: CodeBlockProps) {
  const { copied, copy } = useCopyToClipboard()
  const lines = code.split('\n')
  const highlighted = new Set(highlightLines ?? [])

  return (
    <div className={cn('overflow-hidden rounded-md border border-line bg-surface-muted', className)}>
      {(title || copyable) && (
        <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-1.5">
          <span className="truncate text-2xs font-medium uppercase tracking-wide text-ink-subtle">
            {title}
          </span>
          {copyable ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void copy(code)}
              leadingIcon={copied ? <IconCheck className="h-3.5 w-3.5" /> : <IconCopy className="h-3.5 w-3.5" />}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          ) : null}
        </div>
      )}
      <div className="overflow-auto" style={{ maxHeight }}>
        <pre
          className={cn(
            'min-w-full px-3 py-2.5 font-mono text-xs leading-relaxed text-ink',
            wrap ? 'whitespace-pre-wrap break-words' : 'whitespace-pre',
          )}
        >
          {showLineNumbers || highlighted.size > 0 ? (
            <code>
              {lines.map((line, index) => {
                const lineNumber = index + 1
                const isHighlighted = highlighted.has(lineNumber)
                return (
                  <span
                    key={lineNumber}
                    className={cn(
                      'block',
                      isHighlighted && '-mx-3 border-l-2 border-danger bg-danger-soft px-[calc(0.75rem-2px)]',
                    )}
                  >
                    {showLineNumbers ? (
                      <span className="mr-3 inline-block w-8 select-none text-right text-ink-subtle">
                        {lineNumber}
                      </span>
                    ) : null}
                    {line || ' '}
                  </span>
                )
              })}
            </code>
          ) : (
            <code>{code}</code>
          )}
        </pre>
      </div>
    </div>
  )
}

/** Inline monospace for a short value in a sentence. */
export function InlineCode({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <code className={cn('rounded bg-surface-muted px-1 py-0.5 font-mono text-[0.8125em] text-ink', className)}>
      {children}
    </code>
  )
}

/** Pretty-printed JSON, for evidence blobs and payloads. */
export function JsonBlock({
  value,
  title,
  className,
  maxHeight,
}: {
  value: unknown
  title?: ReactNode
  className?: string
  maxHeight?: string
}) {
  let text: string
  try {
    text = JSON.stringify(value, null, 2) ?? 'null'
  } catch {
    text = String(value)
  }
  return <CodeBlock code={text} title={title} className={className} maxHeight={maxHeight} wrap />
}
